import type { RoundSnapshotV3 } from "../round/round-types.js";
import type { ContestSnapshot } from "./contest-types.js";
import { selectContests } from "./contest-selectors.js";
import { selectContestProgress } from "./contest-result.js";

export function replaceContest(
  snapshot: RoundSnapshotV3,
  updated: ContestSnapshot,
): RoundSnapshotV3 {
  if (snapshot.mainContest.id === updated.id)
    return { ...snapshot, mainContest: updated };
  return {
    ...snapshot,
    remainderContests: (snapshot.remainderContests ?? []).map((contest) =>
      contest.id === updated.id ? updated : contest,
    ),
  };
}

/** Once decided, a contest result is not recalculated by later physical scores. */
export function freezeFinishedMatches(
  snapshot: RoundSnapshotV3,
): RoundSnapshotV3 {
  let next = snapshot;
  for (const contest of selectContests(next)) {
    if (contest.format !== "match-play" || contest.result) continue;
    const progress = provisionalContestProgress(next, contest);
    if (progress.complete)
      next = replaceContest(next, { ...contest, result: progress.result });
  }
  return next;
}

/** A later agreement keeps its physical holes when earlier scores are edited. */
function provisionalContestProgress(
  snapshot: RoundSnapshotV3,
  contest: ContestSnapshot,
) {
  const child = selectContests(snapshot).find(
    (item) => item.parentContestId === contest.id,
  );
  if (!child) return selectContestProgress(snapshot, contest.id);
  const boundary = contest.holeNumbers.indexOf(child.holeNumbers[0]!);
  if (boundary <= 0) return selectContestProgress(snapshot, contest.id);
  const candidate = structuredClone(snapshot);
  for (const entries of Object.values(candidate.holeEntries))
    for (const number of contest.holeNumbers.slice(boundary))
      delete entries[number];
  const progress = selectContestProgress(candidate, contest.id);
  if (progress.complete) return progress;
  // The parties already moved to a new contest at this boundary. Re-score the
  // original segment there; never borrow the child's strokes to decide it.
  return selectContestProgress(
    replaceContest(candidate, {
      ...contest,
      holeNumbers: contest.holeNumbers.slice(0, boundary),
    }),
    contest.id,
  );
}
